'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Heading } from '../ui';
import { useInView } from '@/lib/useInView';
import CTAButton from '../ui/CTAButton';

/**
 * Hook for animating a number from 0 to a target value
 */
const useCountUp = ({
  target,
  duration = 2000,
  delay = 0,
  easing = 'easeOutCubic',
  shouldStart = false,
  prefersReducedMotion = false
}: {
  target: number;
  duration?: number;
  delay?: number;
  easing?: 'linear' | 'easeOutCubic' | 'easeInOutCubic';
  shouldStart: boolean;
  prefersReducedMotion?: boolean;
}): number => {
  const [count, setCount] = useState(0);
  const animationFrameRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    // If reduced motion, immediately set to target value
    if (prefersReducedMotion && shouldStart) {
      setCount(target);
      return;
    }

    if (!shouldStart) return;

    const easingFunctions = {
      linear: (t: number) => t,
      easeOutCubic: (t: number) => 1 - Math.pow(1 - t, 3),
      easeInOutCubic: (t: number) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
    };

    const animate = (timestamp: number) => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp;
      }

      const elapsed = timestamp - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easingFunctions[easing](progress);
      const currentCount = Math.floor(easedProgress * target);

      setCount(currentCount);

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        setCount(target);
        animationFrameRef.current = null;
      }
    };

    const startAnimation = () => {
      const timeoutId = setTimeout(() => {
        startTimeRef.current = null;
        animationFrameRef.current = requestAnimationFrame(animate);
      }, delay);

      return timeoutId;
    };

    const timeoutId = startAnimation();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [shouldStart, target, duration, delay, easing, prefersReducedMotion]);

  return count;
};

/**
 * Formats a number with commas for thousands separator
 */
const formatNumberWithCommas = (num: number): string => {
  return Math.floor(num).toLocaleString('en-US');
};

/**
 * Global Network Heading component with animate-in-view
 */
function GlobalNetworkHeading() {
  const { ref } = useInView<HTMLDivElement>();
  
  return (
    <div ref={ref} className="animate-in-view">
      <Heading title={"Our global CX network"} />
    </div>
  );
}

/**
 * Global Network Body component with animate-in-view
 */
function GlobalNetworkBody() {
  const { ref } = useInView<HTMLParagraphElement>();
  
  return (
    <p ref={ref} className="body-default max-w-5xl pt-[1.25rem] lg:pt-[3.125rem] pb-[1.875rem] lg:pb-[3.4375rem] animate-in-view">
      We&rsquo;re part of a global network of idea people and experience builders using rich insights, user-centric tech, and experience-led design to transform how brands and people connect. Our U.S. headquarters is located in New York, with additional offices in Chicago, Miami and Boston.
    </p>
  );
}

/**
 * StatCard component with animate-in-view wrapper
 */
function StatCard({ children }: { children: React.ReactNode }) {
  const { ref } = useInView<HTMLDivElement>();
  
  return (
    <div ref={ref} className="animate-in-view">
      {children}
    </div>
  );
}

/**
 * CountUpStat component for animating individual statistics
 */
const CountUpStat = ({
  target,
  suffix = '',
  delay = 0,
  className = '',
  as: Component = 'h2',
  shouldStart = false,
  prefersReducedMotion = false
}: {
  target: number;
  suffix?: string;
  delay?: number;
  className?: string;
  as?: 'h2' | 'h3';
  shouldStart?: boolean;
  prefersReducedMotion?: boolean;
}) => {
  const elementRef = useRef<HTMLHeadingElement>(null);

  const count = useCountUp({
    target,
    duration: 2000,
    delay,
    easing: 'easeOutCubic',
    shouldStart,
    prefersReducedMotion
  });

  return (
    <Component ref={elementRef} className={className} aria-live="polite" aria-atomic="true">
      {formatNumberWithCommas(count)}{suffix}
    </Component>
  );
};

export type CountUpStatItem = {
  target: number;
  suffix?: string;
  delay?: number;
  className?: string;
  as?: 'h2' | 'h3';
  label: string;
};

export type CountUpStatListProps = {
  stats: CountUpStatItem[];
  ctaButton?: {
    href: string;
    text: string;
    onClick?: () => void;
  };
  showDividers?: boolean;
  className?: string;
};

/**
 * CountUpStatList - A component that displays a list of animated count-up statistics
 * 
 * @component
 * @example
 * ```tsx
 * <CountUpStatList
 *   stats={[
 *     { target: 2300, suffix: '+', delay: 0, label: 'people' },
 *     { target: 19, delay: 150, label: 'countries' },
 *     { target: 26, delay: 300, label: 'offices' }
 *   ]}
 *   ctaButton={{
 *     href: 'https://example.com',
 *     text: 'Learn more',
 *     onClick: () => console.log('clicked')
 *   }}
 * />
 * ```
 */
export default function CountUpStatList({
  stats,
  ctaButton,
  showDividers = true,
  className = ''
}: CountUpStatListProps) {
  const statsSectionRef = useRef<HTMLDivElement>(null);
  const [shouldAnimateStats, setShouldAnimateStats] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Check for reduced motion preference
  useEffect(() => {
    const checkReducedMotion = () => {
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      setPrefersReducedMotion(mediaQuery.matches);
      // If reduced motion, immediately trigger stats animation
      if (mediaQuery.matches) {
        setShouldAnimateStats(true);
      }
    };
    
    checkReducedMotion(); // Initial check
    
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = () => {
      setPrefersReducedMotion(mediaQuery.matches);
      if (mediaQuery.matches) {
        setShouldAnimateStats(true);
      }
    };
    
    // Listen for changes (some browsers support this)
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  // Trigger stats animation when stats section scrolls into view
  useEffect(() => {
    if (!statsSectionRef.current || prefersReducedMotion) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !shouldAnimateStats) {
            setShouldAnimateStats(true);
            observer.disconnect();
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '0px'
      }
    );

    observer.observe(statsSectionRef.current);

    return () => {
      observer.disconnect();
    };
  }, [shouldAnimateStats, prefersReducedMotion]);

  return (
    <div className={`relative max-w-8xl mx-auto global-padding md:global-padding-0 mb-24 ${className}`}>
      <GlobalNetworkHeading />
      <GlobalNetworkBody />
      <div ref={statsSectionRef} className="flex flex-col">
        <div className="flex flex-col">
          {stats.map((stat, index) => (
            <React.Fragment key={index}>
              <StatCard>
                <div className={`flex flex-col md:flex-row justify-between items-start md:items-end mb-[0.3125rem] lg:mb-[0.875rem] ${index > 0 ? 'lg:pt-[3.6875rem]' : ''}`}>
                  <CountUpStat
                    target={stat.target}
                    suffix={stat.suffix}
                    delay={stat.delay}
                    className={stat.className || 'stat-caption text-[#328CF5]'}
                    as={stat.as}
                    shouldStart={shouldAnimateStats}
                    prefersReducedMotion={prefersReducedMotion}
                  />
                  <p className="body-lg mb-1 lg:mr-4">{stat.label}</p>
                </div>
              </StatCard>
              {showDividers && index < stats.length - 1 && (
                <hr className="border-black mb-8" />
              )}
            </React.Fragment>
          ))}
          {showDividers && stats.length > 0 && (
            <hr className="border-black" />
          )}
        </div>
        {ctaButton && (
          <div className="cta-button mt-[1.875rem] lg:mt-[3.875rem] mx-auto">
            <CTAButton 
              href={ctaButton.href} 
              showArrow
              onClick={ctaButton.onClick}
            >
              {ctaButton.text}
            </CTAButton>
          </div>
        )}
      </div>
    </div>
  );
}

